/**
 * Age Verification Popup - JavaScript Handler
 * Complete functionality for age verification with cookie management
 */

class AgeVerificationPopup {
  constructor(options = {}) {
    this.container = document.getElementById(options.containerId);
    this.ageLimit = options.ageLimit || 18;
    this.cookieDays = options.cookieDays || 30;
    this.verifyBirthDate = options.verifyBirthDate !== false;
    this.declineRedirectUrl = options.declineRedirectUrl || '/';
    this.onVerified = options.onVerified || null;

    if (!this.container) return;

    this.init();
  }

  init() {
    // Check if already verified
    if (this.isVerified()) {
      this.hidePopup();
      return;
    }

    // Show popup
    this.showPopup();

    // Bind events
    this.bindEvents();

    // Prevent body scroll when popup is shown
    this.preventBodyScroll();
  }

  bindEvents() {
    if (this.verifyBirthDate) {
      // Birth date verification flow
      const confirmBtn = document.getElementById('age-verify-confirm-btn');
      const declineBtn = document.getElementById('age-verify-decline-btn');

      if (confirmBtn) {
        confirmBtn.addEventListener('click', () => this.handleBirthDateVerification());
      }

      if (declineBtn) {
        declineBtn.addEventListener('click', () => this.handleDecline());
      }

      // Allow Enter key to submit
      const inputs = document.querySelectorAll('.age-popup-input');
      inputs.forEach((input, index) => {
        input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            this.handleBirthDateVerification();
          }
        });

        // Auto-focus to next input
        input.addEventListener('input', (e) => {
          if (e.target.value.length === this.getInputMaxLength(e.target.id)) {
            const nextInput = inputs[index + 1];
            if (nextInput) nextInput.focus();
          }
        });
      });
    } else {
      // Simple yes/no flow
      const simpleBtn = document.getElementById('age-verify-simple-btn');
      const declineBtn = document.getElementById('age-verify-decline-btn');

      if (simpleBtn) {
        simpleBtn.addEventListener('click', () => this.handleVerify());
      }

      if (declineBtn) {
        declineBtn.addEventListener('click', () => this.handleDecline());
      }
    }
  }

  getInputMaxLength(inputId) {
    if (inputId === 'birth-month' || inputId === 'birth-day') return 2;
    if (inputId === 'birth-year') return 4;
    return 2;
  }

  handleBirthDateVerification() {
    const month = document.getElementById('birth-month')?.value;
    const day = document.getElementById('birth-day')?.value;
    const year = document.getElementById('birth-year')?.value;

    if (!this.validateBirthDate(month, day, year)) {
      this.showError();
      return;
    }

    const birthDate = new Date(year, month - 1, day);
    const age = this.calculateAge(birthDate);

    if (age >= this.ageLimit) {
      this.handleVerify();
    } else {
      this.showError();
    }
  }

  validateBirthDate(month, day, year) {
    if (!month || !day || !year) return false;

    const m = parseInt(month);
    const d = parseInt(day);
    const y = parseInt(year);

    // Validate month
    if (m < 1 || m > 12) return false;

    // Validate day
    if (d < 1 || d > 31) return false;

    // Validate year
    const currentYear = new Date().getFullYear();
    if (y < 1900 || y > currentYear) return false;

    // Validate actual date
    const date = new Date(y, m - 1, d);
    if (date.getMonth() !== m - 1) return false;

    return true;
  }

  calculateAge(birthDate) {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  }

  showError() {
    const errorMsg = document.getElementById('age-error-message');
    if (errorMsg) {
      errorMsg.style.display = 'block';
      // Clear inputs
      document.getElementById('birth-month').value = '';
      document.getElementById('birth-day').value = '';
      document.getElementById('birth-year').value = '';
      // Focus on first input
      document.getElementById('birth-month').focus();

      // Hide error after 3 seconds
      setTimeout(() => {
        errorMsg.style.display = 'none';
      }, 3000);
    }
  }

  handleVerify() {
    this.setCookie('age_verified', 'true', this.cookieDays);
    this.hidePopup();
    this.allowBodyScroll();

    if (this.onVerified) {
      this.onVerified();
    }

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('ageVerificationComplete'));
  }

  handleDecline() {
    // Redirect to decline URL
    if (this.declineRedirectUrl) {
      window.location.href = this.declineRedirectUrl;
    }
  }

  isVerified() {
    return this.getCookie('age_verified') === 'true';
  }

  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }

    document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }

    return null;
  }

  showPopup() {
    if (this.container) {
      this.container.style.display = 'flex';
      // Trigger animation
      setTimeout(() => {
        this.container.classList.add('visible');
      }, 10);
    }
  }

  hidePopup() {
    if (this.container) {
      this.container.classList.remove('visible');
      setTimeout(() => {
        this.container.style.display = 'none';
      }, 300);
    }
  }

  preventBodyScroll() {
    document.body.style.overflow = 'hidden';
  }

  allowBodyScroll() {
    document.body.style.overflow = '';
  }
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AgeVerificationPopup;
}
